/* ============================================================
   Lost Collective · Article tap-to-zoom lightbox
   File: assets/lc-article-lightbox.js

   Builds one persistent .lc-lightbox dialog on the body, then
   binds click → open on every paper-mode figure.fig .img inside
   the rebuilt #articleBody (scrolly frames and the hero are
   already full-bleed and are skipped). Waits on
   window.LCArticle.onReady so figures exist before binding.

   Open (Brett, 2026-09-26: "it should animate into a full screen
   view in an eased, smooth and sophisticated manner, and the
   caption should display either beneath it or as an overlay"):
     1. A clone of the clicked image, on the rendition the page has
        already loaded, is placed over the original's exact rect.
     2. It moves to the fitted rect by transform alone (FLIP:
        translate + scale), 420ms on cubic-bezier(0.22, 1, 0.36, 1),
        while the dark ground fades in over the same time. The
        original is hidden in place, so the page never shifts.
     3. Once it lands, a larger rendition is decoded off-screen and
        swapped in. The page serves 800px; the zoom asks for the
        smallest width at or above the fitted width x DPR.
     4. The caption fades in beneath it, or over the foot of the
        image on a phone where beneath would shrink it below 55% of
        the viewport height.
   Close (Esc, backdrop, Close, or the image itself) fades the
   caption out, re-measures the original, and runs the move back.
   ============================================================ */

(function () {
  'use strict';

  window.LCArticle = window.LCArticle || (function () {
    var state = { ready: false, queue: [] };
    return {
      onReady: function (fn) { if (state.ready) fn(); else state.queue.push(fn); },
      fireReady: function () {
        if (state.ready) return;
        state.ready = true;
        state.queue.forEach(function (fn) { try { fn(); } catch (_e) {} });
        state.queue.length = 0;
      }
    };
  })();

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var MOVE_MS = 420;
  var CAP_DELAY_MS = 120;
  var CAP_IN_MS = 180;
  var CAP_OUT_MS = 80;
  var REDUCED_MS = 150;
  var MAX_RENDITION = 3000;
  // Widths offered beyond the page's own srcset. Pair, duo, tri and column
  // frames stop at 1200w because that is all their page slot needs; a zoomed
  // frame needs up to the viewport's width in device pixels.
  var ZOOM_LADDER = [1600, 2000, 2400, 2800, 3000];
  var CAP_GAP = 14;
  var OVERLAY_BELOW = 0.55;

  function px(n) { return Math.round(n * 100) / 100 + 'px'; }

  function canAnimate(el) { return !!(el && typeof el.animate === 'function'); }

  // --- rendition choice -----------------------------------------------------

  function parseSrcset(s) {
    var out = [];
    (s || '').split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      var w = bits[1] && /^\d+w$/.test(bits[1]) ? parseInt(bits[1], 10) : 0;
      if (bits[0] && w) out.push({ url: bits[0], w: w });
    });
    return out;
  }

  function stripWidth(url) {
    return url.replace(/([?&])width=\d+(&|$)/, function (_m, lead, tail) {
      return tail ? lead : '';
    }).replace(/[?&]$/, '');
  }

  function withWidth(base, w) {
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  // The srcset the browser is actually choosing from: a <picture> <source>
  // whose media matches wins over the <img>'s own. Its intrinsic width comes
  // from the matching element's width attribute (lco.photographs), because
  // Shopify never upscales and a larger request returns the same pixels.
  function activeSet(img) {
    var pic = img.parentElement && img.parentElement.tagName === 'PICTURE' ? img.parentElement : null;
    if (pic) {
      var sources = pic.querySelectorAll('source');
      for (var i = 0; i < sources.length; i++) {
        var m = sources[i].getAttribute('media');
        if (!m || window.matchMedia(m).matches) {
          return {
            list: parseSrcset(sources[i].getAttribute('srcset')),
            intrinsic: parseInt(sources[i].getAttribute('width'), 10) || 0
          };
        }
      }
    }
    return {
      list: parseSrcset(img.getAttribute('srcset')),
      intrinsic: parseInt(img.getAttribute('width'), 10) || 0
    };
  }

  function pickRendition(img, cssWidth) {
    var current = img.currentSrc || img.src || '';
    var set = activeSet(img);
    if (!set.list.length) return null;
    var widest = 0;
    set.list.forEach(function (c) { if (c.w > widest) widest = c.w; });
    var list = set.list.filter(function (c) { return c.w <= MAX_RENDITION; });
    var top = 0;
    list.forEach(function (c) { if (c.w > top) top = c.w; });
    var ceiling = Math.min(MAX_RENDITION, set.intrinsic || widest);
    if (current.indexOf('cdn.shopify.com') !== -1 && ceiling > top) {
      var base = stripWidth(current);
      ZOOM_LADDER.forEach(function (w) {
        if (w > top && w < ceiling) list.push({ url: withWidth(base, w), w: w });
      });
      list.push({ url: withWidth(base, ceiling), w: ceiling });
    }
    if (!list.length) return null;
    list.sort(function (a, b) { return a.w - b.w; });
    var need = Math.min(MAX_RENDITION, Math.ceil(cssWidth * (window.devicePixelRatio || 1)));
    var pick = list[list.length - 1];
    for (var i = 0; i < list.length; i++) {
      if (list[i].w >= need) { pick = list[i]; break; }
    }
    // Never trade what is already on screen for something smaller.
    var have = /[?&]width=(\d+)/.exec(current);
    if (have && pick.w <= parseInt(have[1], 10)) return null;
    return pick;
  }

  // --- caption --------------------------------------------------------------

  // The figcaption that belongs to this .img: the nearest ancestor (up to the
  // figure) carrying one. A duo stack carries two for its two images, in
  // image order, so the index of the image picks the caption.
  function captionFor(wrap) {
    var fig = wrap.closest('figure.fig');
    var node = wrap.parentElement;
    while (node) {
      var caps = [];
      var credit = null;
      for (var i = 0; i < node.children.length; i++) {
        var ch = node.children[i];
        if (ch.tagName === 'FIGCAPTION') caps.push(ch);
        else if (ch.classList.contains('lc-fig-credit')) credit = ch;
      }
      if (caps.length) {
        var imgs = Array.prototype.slice.call(node.querySelectorAll('.img'));
        var idx = Math.max(0, imgs.indexOf(wrap));
        return { cap: caps[Math.min(idx, caps.length - 1)], credit: imgs.length === 1 ? credit : null };
      }
      if (node === fig) break;
      node = node.parentElement;
    }
    return { cap: null, credit: null };
  }

  function text(el) { return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : ''; }

  function titleFor(wrap, img, parts) {
    var t = wrap.getAttribute('data-title');
    if (t) return t;
    if (parts.lbl && !/^frame\s+\d+/i.test(parts.lbl) && !/^ref/i.test(parts.lbl)) return parts.lbl;
    return img.alt || parts.lbl || 'Photograph';
  }

  function captionParts(wrap) {
    var found = captionFor(wrap);
    var cap = found.cap;
    var parts = { lbl: '', desc: '', meta: '', credit: null };
    if (cap) {
      var lbl = cap.querySelector('.lbl');
      var desc = cap.querySelector('.desc');
      var meta = cap.querySelector('.meta');
      if (lbl || desc || meta) {
        parts.lbl = text(lbl);
        parts.desc = text(desc);
        parts.meta = text(meta);
      } else {
        parts.desc = text(cap);
      }
    }
    if (found.credit && text(found.credit)) parts.credit = found.credit;
    return parts;
  }

  function init() {
    var root = document.getElementById('articleBody');
    if (!root) return;

    var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    function reduced() { return !!(reduceMq && reduceMq.matches); }

    var lb = document.createElement('div');
    // lc-on-dark: the sitewide on-dark convention, so a credit link in the
    // caption takes the paper focus ring the Close button already has.
    lb.className = 'lc-lightbox lc-on-dark';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Photograph');
    lb.hidden = true;

    var ground = document.createElement('div');
    ground.className = 'lb-ground';
    lb.appendChild(ground);

    var cap = document.createElement('div');
    cap.className = 'lb-cap';
    cap.id = 'lc-lightbox-caption';
    var capIn = document.createElement('div');
    capIn.className = 'lb-cap__in';
    cap.appendChild(capIn);
    lb.appendChild(cap);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'lc-btn lc-btn--icon lc-btn--on-dark lb-close';
    closeBtn.textContent = 'Close';
    lb.appendChild(closeBtn);
    document.body.appendChild(lb);

    // phase: closed | opening | open | closing, mirrored on data-state
    var s = { phase: 'closed', seq: 0 };
    var anims = [];
    function setPhase(p) {
      s.phase = p;
      lb.setAttribute('data-state', p);
    }
    setPhase('closed');

    function stopAnims() {
      anims.forEach(function (a) { try { a.cancel(); } catch (_e) {} });
      anims = [];
    }

    // Every animation stays listed until stopAnims cancels it. A finished
    // fill:forwards fade is still in effect, and one left over from a close
    // would hold the ground and caption at opacity 0 on the next open.
    function run(el, frames, opts) {
      if (!canAnimate(el)) return Promise.resolve();
      var a = el.animate(frames, opts);
      anims.push(a);
      return a.finished.then(function () {}, function () {});
    }

    function fillCaption(parts) {
      capIn.textContent = '';
      function add(cls, value) {
        if (!value) return;
        var span = document.createElement('span');
        span.className = cls;
        span.textContent = value;
        capIn.appendChild(span);
      }
      add('lb-lbl', parts.lbl);
      add('lb-desc', parts.desc);
      add('lb-meta', parts.meta);
      if (parts.credit) {
        var credit = document.createElement('span');
        credit.className = 'lb-credit';
        var link = parts.credit.querySelector('a');
        if (link) {
          var a = document.createElement('a');
          a.href = link.href;
          a.rel = 'noopener';
          a.target = '_blank';
          a.textContent = text(link);
          credit.appendChild(a);
        } else {
          credit.textContent = text(parts.credit);
        }
        capIn.appendChild(credit);
      }
      var has = capIn.children.length > 0;
      cap.hidden = !has;
      if (has) lb.setAttribute('aria-describedby', cap.id);
      else lb.removeAttribute('aria-describedby');
      return has;
    }

    // --- scroll lock ---------------------------------------------------------
    // The root carries scrollbar-gutter: stable (lc-tokens.css), so hiding its
    // overflow keeps the gutter and nothing moves. A browser without gutter
    // support widens the layout by the scrollbar; the body is padded by
    // exactly the measured difference so the article stays where it was.
    // Measured on the body's laid-out width, not the root's clientWidth:
    // Chromium reports clientWidth without the gutter once overflow is hidden
    // while still laying the page out inside it, and padding by that phantom
    // 15px is what would move the article.
    //
    // With a classic scrollbar the kept gutter lies outside every fixed
    // element, so the dark ground cannot reach it and it would show the page's
    // paper as a strip beside the zoom. It is painted by the root background
    // instead, faded on the ground's own timing. The root is transparent and
    // the body paints the page, so a colour on the root only ever shows in the
    // gutter; if either is not so, or the scrollbar overlays (no gutter), the
    // root is left alone.
    var padBefore = null;
    var gutter = null;
    function rgba(str) {
      var m = String(str).match(/[\d.]+/g) || [];
      return [+m[0] || 0, +m[1] || 0, +m[2] || 0, m[3] == null ? (m.length ? 1 : 0) : +m[3]];
    }
    function lock() {
      var de = document.documentElement;
      gutter = null;
      // The gutter is read off the layout, not clientWidth: with scrollbars
      // hidden (headless Chromium, where every CI and probe screenshot is
      // taken) clientWidth reports none while the page is still laid out
      // 15px short of the window.
      if (window.innerWidth - de.getBoundingClientRect().width > 0.5 && canAnimate(de)) {
        var root = rgba(getComputedStyle(de).backgroundColor);
        var paper = getComputedStyle(document.body).backgroundColor;
        var p = rgba(paper);
        var g = rgba(getComputedStyle(ground).backgroundColor);
        if (root[3] === 0 && p[3] === 1) {
          gutter = {
            paper: paper,
            dark: 'rgb(' + [0, 1, 2].map(function (i) {
              return Math.round(g[i] * g[3] + p[i] * (1 - g[3]));
            }).join(', ') + ')'
          };
        }
      }
      var before = document.body.getBoundingClientRect().width;
      de.classList.add('lc-lightbox-lock');
      var grew = document.body.getBoundingClientRect().width - before;
      if (grew > 0) {
        padBefore = document.body.style.paddingRight;
        var cur = parseFloat(getComputedStyle(document.body).paddingRight) || 0;
        document.body.style.paddingRight = (cur + grew) + 'px';
      }
    }
    function unlock() {
      document.documentElement.classList.remove('lc-lightbox-lock');
      if (gutter) {
        document.documentElement.style.backgroundColor = '';
        gutter = null;
      }
      if (padBefore !== null) {
        document.body.style.paddingRight = padBefore;
        padBefore = null;
      }
    }

    function fadeGutter(show, timing) {
      if (!gutter) return null;
      var de = document.documentElement;
      var a = show ? gutter.paper : gutter.dark;
      var b = show ? gutter.dark : gutter.paper;
      de.style.backgroundColor = b;
      return run(de, [{ backgroundColor: a }, { backgroundColor: b }], timing);
    }

    // --- geometry --------------------------------------------------------------

    function aspectOf(img) {
      var w = img.naturalWidth || parseInt(img.getAttribute('width'), 10) || 0;
      var h = img.naturalHeight || parseInt(img.getAttribute('height'), 10) || 0;
      if (w && h) return w / h;
      var r = img.getBoundingClientRect();
      return r.width && r.height ? r.width / r.height : 1.5;
    }

    // Where the whole photograph is drawn on the page, and the part of it the
    // reader can see. They differ only for an object-fit crop or a clipping
    // wrapper; the clone clips to the visible part on its way in and out.
    function pageRects(img, ar) {
      var r = img.getBoundingClientRect();
      var content = { left: r.left, top: r.top, width: r.width, height: r.height };
      var fit = getComputedStyle(img).objectFit;
      var boxAr = r.width / (r.height || 1);
      if ((fit === 'cover' || fit === 'contain') && Math.abs(boxAr - ar) > 0.01) {
        // cover fills the box on its tighter side, contain on its looser one
        var widthBound = fit === 'cover' ? boxAr > ar : boxAr < ar;
        var cw = widthBound ? r.width : r.height * ar;
        var ch = widthBound ? r.width / ar : r.height;
        content = { left: r.left + (r.width - cw) / 2, top: r.top + (r.height - ch) / 2, width: cw, height: ch };
      }
      var vis = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
      var wrap = img.closest('.img');
      if (wrap) {
        var w = wrap.getBoundingClientRect();
        vis.left = Math.max(vis.left, w.left);
        vis.top = Math.max(vis.top, w.top);
        vis.right = Math.min(vis.right, w.right);
        vis.bottom = Math.min(vis.bottom, w.bottom);
      }
      return { content: content, vis: vis, rect: r };
    }

    function measureCap(width, overlay) {
      if (cap.hidden) return 0;
      cap.classList.toggle('is-overlay', !!overlay);
      cap.style.width = px(width);
      return cap.offsetHeight;
    }

    // The largest rect the photograph can take with its caption beneath it,
    // or, on a portrait phone where that would leave the photograph under 55%
    // of the viewport height, with the caption laid over its foot instead.
    // The photograph is never shrunk to make room for the Close button: a
    // lone plate is already sized to 92vh on the page, and a zoom must not
    // come out smaller than the frame it came from. Where the photograph
    // would run under the button it moves down into whatever vertical slack
    // there is; where there is not enough, the button sits over its corner
    // on a dark backing.
    function fit(ar) {
      var vw = lb.clientWidth;
      var vh = lb.clientHeight;
      var pad = vw < 750 ? 16 : 24;
      var t = fitWithin(ar, vw, vh, pad);
      var btn = closeBtn.getBoundingClientRect();
      var clearY = btn.bottom + 8;
      var under = t.x + t.w > btn.left - 8 && t.y < clearY;
      if (under) {
        var slack = vh - pad - (t.y + t.blockH);
        var d = Math.max(0, Math.min(clearY - t.y, slack));
        t.y += d;
        if (t.capY != null) t.capY += d;
        under = t.y < clearY;
      }
      t.underClose = under;
      return t;
    }

    function fitWithin(ar, vw, vh, pad) {
      var narrow = vw < 750;
      // Side insets sit inside the page's own 4vw margin (92vw frames), so a
      // frame that fills the width on the page is never narrower zoomed.
      var padX = narrow ? 8 : 24;
      var padTop = pad;
      var padBottom = pad;
      var availW = Math.max(40, vw - padX * 2);
      var availH = Math.max(40, vh - padTop - padBottom);

      function size(maxH) {
        var w = Math.min(availW, Math.max(1, maxH) * ar);
        return { w: w, h: w / ar };
      }

      var full = size(availH);
      var below = full;
      var capH = 0;
      if (!cap.hidden) {
        // A caption only grows as it narrows, so raising the reserve until it
        // holds is guaranteed to converge and never to overflow.
        capH = measureCap(full.w, false);
        below = size(availH - capH - CAP_GAP);
        for (var i = 0; i < 6; i++) {
          var m = measureCap(below.w, false);
          if (m <= capH) break;
          capH = m;
          below = size(availH - capH - CAP_GAP);
        }
      }

      var overlay = false;
      if (!cap.hidden && narrow && vh > vw && below.h < OVERLAY_BELOW * vh && below.h < full.h - 1) {
        overlay = true;
      }

      var box = overlay ? full : below;
      var blockH = box.h + (!cap.hidden && !overlay ? CAP_GAP + capH : 0);
      var x = (vw - box.w) / 2;
      var y = padTop + Math.max(0, (availH - blockH) / 2);
      var t = { x: x, y: y, w: box.w, h: box.h, overlay: overlay, blockH: blockH };
      if (!cap.hidden) {
        var oh = measureCap(box.w, overlay);
        t.capX = x;
        t.capY = overlay ? y + box.h - oh : y + box.h + CAP_GAP;
      }
      return t;
    }

    function place(clone, t) {
      lb.classList.toggle('is-under-close', !!t.underClose);
      clone.style.width = px(t.w);
      clone.style.height = px(t.h);
      clone.style.transform = 'translate(' + px(t.x) + ',' + px(t.y) + ')';
      if (!cap.hidden) {
        cap.classList.toggle('is-overlay', t.overlay);
        cap.style.width = px(t.w);
        cap.style.transform = 'translate(' + px(t.capX) + ',' + px(t.capY) + ')';
      }
    }

    // The transform and clip that lay the clone (sized to the fitted rect t)
    // exactly over the photograph on the page. Scaled per axis: the page box
    // can differ from the file's aspect by a fraction of a pixel (its height
    // comes from the declared width and height), and starting on the box
    // itself is what keeps the first frame from twitching.
    function pageState(t, rects) {
      var c = rects.content;
      var kx = c.width / t.w;
      var ky = c.height / t.h;
      var v = rects.vis;
      var inset = [
        Math.max(0, (v.top - c.top) / ky),
        Math.max(0, (c.left + c.width - v.right) / kx),
        Math.max(0, (c.top + c.height - v.bottom) / ky),
        Math.max(0, (v.left - c.left) / kx)
      ];
      return {
        transform: 'translate(' + px(c.left) + ',' + px(c.top) + ') scale(' + kx + ',' + ky + ')',
        clip: inset.some(function (n) { return n > 0.5; })
          ? 'inset(' + inset.map(px).join(' ') + ')'
          : null
      };
    }

    // Against the viewport, not the dialog: the dialog is display:none until
    // this has answered.
    function onScreen(r) {
      var de = document.documentElement;
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 &&
        r.top < de.clientHeight && r.left < de.clientWidth;
    }

    // --- rendition upgrade -------------------------------------------------------

    function upgrade(img, t) {
      var pick = pickRendition(img, t.w);
      var clone = s.clone;
      if (!pick || !clone || pick.url === clone.currentSrc || pick.url === clone.src) return;
      var token = s.seq;
      var hi = new Image();
      hi.alt = clone.alt;
      hi.src = pick.url;
      var decoded = typeof hi.decode === 'function' ? hi.decode() : new Promise(function (res, rej) {
        hi.onload = res; hi.onerror = rej;
      });
      decoded.then(function () {
        if (token !== s.seq || s.phase !== 'open' || s.clone !== clone) return;
        hi.className = clone.className;
        hi.style.cssText = clone.style.cssText;
        hi.draggable = false;
        clone.parentNode.replaceChild(hi, clone);
        s.clone = hi;
      }, function () {});
    }

    // --- open / close --------------------------------------------------------------

    function open(wrap) {
      if (s.phase !== 'closed') return;
      var img = wrap.querySelector('img');
      if (!img) return;
      var ar = aspectOf(img);
      var rects = pageRects(img, ar);
      if (!onScreen(rects.rect)) return;

      var parts = captionParts(wrap);
      stopAnims();
      s.seq++;
      setPhase('opening');
      s.trigger = wrap;
      s.img = img;
      s.pendingClose = false;
      s.pendingRefit = false;

      lb.setAttribute('aria-label', titleFor(wrap, img, parts));
      fillCaption(parts);
      lock();

      var clone = document.createElement('img');
      clone.className = 'lb-img';
      clone.alt = img.alt || '';
      clone.draggable = false;
      clone.src = img.currentSrc || img.src;
      lb.insertBefore(clone, cap);
      s.clone = clone;

      lb.hidden = false;
      cap.style.opacity = '0';
      var t = fit(ar);
      s.target = t;
      place(clone, t);
      img.style.visibility = 'hidden';
      try { closeBtn.focus({ preventScroll: true }); } catch (_e) { closeBtn.focus(); }

      var done;
      if (reduced()) {
        clone.style.opacity = '1';
        ground.style.opacity = '1';
        cap.style.opacity = '1';
        var fadeIn = { duration: REDUCED_MS, easing: 'linear', fill: 'backwards' };
        done = Promise.all([
          run(ground, [{ opacity: 0 }, { opacity: 1 }], fadeIn),
          fadeGutter(true, fadeIn),
          run(clone, [{ opacity: 0 }, { opacity: 1 }], fadeIn),
          cap.hidden ? null : run(cap, [{ opacity: 0 }, { opacity: 1 }], fadeIn)
        ]);
      } else {
        var from = pageState(t, rects);
        var move = { duration: MOVE_MS, easing: EASE, fill: 'backwards' };
        var k0 = { transform: from.transform };
        var k1 = { transform: clone.style.transform };
        if (from.clip) { k0.clipPath = from.clip; k1.clipPath = 'inset(0px 0px 0px 0px)'; }
        ground.style.opacity = '1';
        done = Promise.all([
          run(clone, [k0, k1], move),
          run(ground, [{ opacity: 0 }, { opacity: 1 }], move),
          fadeGutter(true, move)
        ]);
      }

      var token = s.seq;
      done.then(function () {
        if (token !== s.seq || s.phase !== 'opening') return;
        setPhase('open');
        if (!reduced() && !cap.hidden) {
          cap.style.opacity = '1';
          run(cap, [
            { opacity: 0, transform: cap.style.transform + ' translateY(6px)' },
            { opacity: 1, transform: cap.style.transform }
          ], { duration: CAP_IN_MS, delay: CAP_DELAY_MS, easing: EASE, fill: 'backwards' });
        }
        if (s.pendingRefit) refit();
        upgrade(img, s.target);
        if (s.pendingClose) close();
      });
    }

    function finishClose() {
      stopAnims();
      if (s.img) s.img.style.visibility = '';
      if (s.clone && s.clone.parentNode) s.clone.parentNode.removeChild(s.clone);
      lb.hidden = true;
      ground.style.opacity = '';
      cap.style.opacity = '';
      unlock();
      var trigger = s.trigger;
      setPhase('closed');
      s.clone = s.img = s.trigger = s.target = null;
      if (trigger && typeof trigger.focus === 'function') {
        try { trigger.focus({ preventScroll: true }); } catch (_e) { trigger.focus(); }
      }
    }

    function close() {
      if (s.phase === 'opening') { s.pendingClose = true; return; }
      if (s.phase !== 'open') return;
      setPhase('closing');
      s.seq++;
      var token = s.seq;
      stopAnims();
      var clone = s.clone;
      var img = s.img;

      if (reduced()) {
        var fadeOut = { duration: REDUCED_MS, easing: 'linear', fill: 'forwards' };
        Promise.all([
          run(ground, [{ opacity: 1 }, { opacity: 0 }], fadeOut),
          fadeGutter(false, fadeOut),
          run(clone, [{ opacity: 1 }, { opacity: 0 }], fadeOut),
          cap.hidden ? null : run(cap, [{ opacity: 1 }, { opacity: 0 }], fadeOut)
        ]).then(function () { if (token === s.seq) finishClose(); });
        return;
      }

      var capGone = cap.hidden
        ? Promise.resolve()
        : run(cap, [{ opacity: 1 }, { opacity: 0 }], { duration: CAP_OUT_MS, easing: 'linear', fill: 'forwards' });

      capGone.then(function () {
        if (token !== s.seq) return;
        cap.style.opacity = '0';
        var ar = aspectOf(img);
        // The page may have moved under the dialog (a resize, a font swap):
        // measure where the photograph is now, not where it was.
        var rects = pageRects(img, ar);
        var move = { duration: MOVE_MS, easing: EASE, fill: 'forwards' };
        var moves;
        if (onScreen(rects.rect)) {
          var to = pageState(s.target, rects);
          var k0 = { transform: clone.style.transform };
          var k1 = { transform: to.transform };
          if (to.clip) { k0.clipPath = 'inset(0px 0px 0px 0px)'; k1.clipPath = to.clip; }
          moves = run(clone, [k0, k1], move);
        } else {
          moves = run(clone, [{ opacity: 1 }, { opacity: 0 }], move);
        }
        Promise.all([moves, run(ground, [{ opacity: 1 }, { opacity: 0 }], move), fadeGutter(false, move)])
          .then(function () { if (token === s.seq) finishClose(); });
      });
    }

    function refit() {
      if (s.phase === 'opening') { s.pendingRefit = true; return; }
      if (s.phase !== 'open' || !s.clone) return;
      s.pendingRefit = false;
      // A caption still fading in holds its old position until it finishes.
      stopAnims();
      cap.style.opacity = '1';
      var t = fit(aspectOf(s.img));
      s.target = t;
      place(s.clone, t);
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (s.phase === 'closed') return;
      if (resizeTimer) window.cancelAnimationFrame(resizeTimer);
      resizeTimer = window.requestAnimationFrame(function () { resizeTimer = null; refit(); });
    });

    // Focus stays inside the dialog: Close and any credit link in the caption.
    lb.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || lb.hidden) return;
      var stops = Array.prototype.slice.call(lb.querySelectorAll('button, a[href]'));
      if (!stops.length) return;
      var i = stops.indexOf(document.activeElement);
      var next = e.shiftKey ? i - 1 : i + 1;
      if (next < 0) next = stops.length - 1;
      if (next >= stops.length) next = 0;
      e.preventDefault();
      stops[next].focus();
    });

    lb.addEventListener('click', function (e) {
      if (e.target === closeBtn) { close(); return; }
      // A second click of a double-click lands here mid-flight; it is not a
      // request to close what is still opening.
      if (s.phase === 'opening') return;
      if (e.target === lb || e.target === ground || e.target.classList.contains('lb-img')) close();
    });

    // One-finger drags on the dialog would otherwise scroll the page beneath
    // on browsers that ignore overflow on the root; pinch zoom still works.
    lb.addEventListener('touchmove', function (e) {
      if (e.touches && e.touches.length === 1) e.preventDefault();
    }, { passive: false });

    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && !lb.hidden) close();
    });

    root.querySelectorAll('figure.fig .img').forEach(function (imgDiv) {
      if (imgDiv.closest('.lc-scrolly, .art-hero, figure.fig.hero')) return;
      var img = imgDiv.querySelector('img');
      if (!img) return;
      imgDiv.setAttribute('tabindex', '0');
      imgDiv.setAttribute('role', 'button');
      imgDiv.setAttribute('aria-label', img.alt ? 'Zoom image: ' + img.alt : 'Zoom image');
      imgDiv.addEventListener('click', function (e) {
        e.stopPropagation();
        open(imgDiv);
      });
      imgDiv.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        e.preventDefault();
        e.stopPropagation();
        open(imgDiv);
      });
    });
  }

  ready(function () {
    window.LCArticle.onReady(init);
  });
})();
